import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import { type RecordData } from './RecordData.tsx';
import { libelleRecord } from '../Preferences/VmaRecord.tsx';

// Un record de l'historique (GET /api/preference/records).
export type RecordHistorique = RecordData & {
  id: string;
  date: string;
  vma: number | null;
  courant: boolean;
};

function entetes(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
  };
}

function formaterDate(iso: string): string {
  const [a, m, j] = iso.split('-');
  return a && m && j ? `${j}/${m}/${a}` : iso;
}

// Historique des records saisis (table user_record cote serveur). Un clic
// sur une ligne affiche les allures de ce record (sans l'enregistrer a
// nouveau) ; la corbeille le supprime de l'historique.
export default function HistoriqueRecords({
  rafraichissement,
  onSelection,
  onModification,
}: {
  /** Change a chaque ajout de record, pour recharger l'historique. */
  rafraichissement: number;
  onSelection?: (record: RecordHistorique) => void;
  /** Appele apres une suppression (le record courant / la VMA ont pu changer). */
  onModification?: () => void;
}) {
  const [records, setRecords] = React.useState<RecordHistorique[]>([]);
  const [chargement, setChargement] = React.useState(true);
  const [erreur, setErreur] = React.useState<string | null>(null);

  const charger = React.useCallback(() => {
    setChargement(true);
    fetch('/api/preference/records', { headers: entetes() })
      .then((response) => {
        if (response.status !== 200) throw new Error('statut ' + response.status);
        return response.json() as Promise<RecordHistorique[]>;
      })
      .then((liste) => {
        setRecords(liste);
        setErreur(null);
      })
      .catch((e: Error) => setErreur('Impossible de charger l\'historique des records (' + e.message + ')'))
      .finally(() => setChargement(false));
  }, []);

  React.useEffect(() => {
    charger();
  }, [charger, rafraichissement]);

  const supprimer = (record: RecordHistorique) => {
    fetch('/api/preference/records/' + encodeURIComponent(record.id), { method: 'DELETE', headers: entetes() })
      .then((response) => {
        if (response.status !== 204 && response.status !== 404) throw new Error('statut ' + response.status);
        charger();
        onModification?.();
      })
      .catch((e: Error) => setErreur('Suppression impossible (' + e.message + ')'));
  };

  const columns: GridColDef<RecordHistorique>[] = [
    {
      field: 'date',
      headerName: 'Date',
      width: 120,
      valueFormatter: (value: string) => formaterDate(value),
    },
    {
      field: 'record',
      headerName: 'Record',
      width: 260,
      sortable: false,
      valueGetter: (_value, row) => libelleRecord(row),
    },
    {
      field: 'vma',
      headerName: 'VMA déduite',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number | null) => (value === null || value === undefined ? '' : `${value.toLocaleString('fr-FR')} km/h`),
    },
    {
      field: 'courant',
      headerName: '',
      width: 150,
      sortable: false,
      renderCell: (params) =>
        params.value ? <Chip size="small" color="primary" label="VMA de référence" /> : null,
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      align: 'right',
      renderCell: (params) => (
        <Tooltip title="Supprimer ce record">
          <IconButton
            size="small"
            aria-label="Supprimer ce record"
            onClick={(e) => {
              e.stopPropagation();
              supprimer(params.row);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
      {!chargement && !erreur && records.length === 0 ? (
        <Alert severity="info">Aucun record enregistré pour l'instant.</Alert>
      ) : (
        <DataGrid
          rows={records}
          columns={columns}
          loading={chargement}
          onRowClick={(params) => onSelection?.(params.row)}
          hideFooter={records.length <= 25}
          disableRowSelectionOnClick
          autoHeight
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      )}
    </Box>
  );
}
